// =============================================================================
// BP-05-03: Calendar Frontend - ConflictWarning Component
// =============================================================================

import React from 'react';
import { format } from 'date-fns';
import { AlertTriangle, Clock, Calendar } from 'lucide-react';
import { type AppointmentConflict } from '../types';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

interface ConflictWarningProps {
  conflicts: AppointmentConflict[];
  className?: string;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function ConflictWarning({ conflicts, className }: ConflictWarningProps) {
  if (conflicts.length === 0) return null;

  return (
    <div
      className={cn(
        'p-4 bg-amber-50 border border-amber-200 rounded-xl',
        className
      )}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-amber-900">
            Scheduling Conflict{conflicts.length > 1 ? 's' : ''} Detected
          </h4>
          <p className="text-sm text-amber-700 mt-0.5">
            This appointment overlaps with {conflicts.length} existing{' '}
            {conflicts.length === 1 ? 'appointment' : 'appointments'}:
          </p>

          <div className="mt-3 space-y-2">
            {conflicts.map((conflict) => {
              const apt = conflict.conflictingAppointment;
              const startDate = new Date(apt.startsAt);
              const endDate = new Date(apt.endsAt);

              return (
                <div
                  key={apt.id}
                  className="flex items-center gap-3 p-2 bg-white rounded-lg border border-amber-100"
                >
                  <div
                    className="w-8 h-8 rounded flex items-center justify-center"
                    style={{
                      backgroundColor: `${apt.color || '#C4785A'}20`,
                    }}
                  >
                    <Calendar
                      className="w-4 h-4"
                      style={{ color: apt.color || '#C4785A' }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-amber-900 truncate">
                      {apt.title}
                    </p>
                    <p className="text-xs text-amber-700 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {format(startDate, 'MMM d, h:mm a')} -{' '}
                      {format(endDate, 'h:mm a')}
                    </p>
                  </div>
                  <div className="text-xs text-amber-600 font-medium">
                    {conflict.overlapMinutes} min overlap
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-amber-600 mt-3">
            You can still create this appointment, but be aware of the overlap.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ConflictWarning;
